"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import { trackAnalyticsEvent } from "@/lib/analytics/track";
import { cn } from "@/lib/utils";

export interface IWaitlistSignupProps {
  variant?: "default" | "blog-cta";
  className?: string;
}

/**
 * Embedded waitlist signup for marketing and blog CTAs.
 */
export function WaitlistSignup({
  variant = "default",
  className,
}: IWaitlistSignupProps): React.ReactElement {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const isBlogCta = variant === "blog-cta";

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? "Something went wrong. Please try again.");
      }

      setStatus("success");
      trackAnalyticsEvent("submit_waitlist", {});
      setName("");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong."
      );
      trackAnalyticsEvent("waitlist_error", {
        error_code: err instanceof Error ? err.message : "unknown",
      });
    }
  }

  if (status === "success") {
    return (
      <div
        className={cn(
          "rounded-xl border border-primary/20 bg-primary/5 p-6 text-center",
          className
        )}
      >
        <PhosphorIcon
          name="check-circle"
          className="mx-auto mb-2 h-10 w-10 text-primary"
          aria-hidden="true"
        />
        <p className="text-lg font-semibold text-foreground">
          You&apos;re on the list!
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          We&apos;ll be in touch with early access updates.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        isBlogCta &&
          "rounded-xl border border-border bg-card p-6 shadow-sm",
        className
      )}
    >
      {isBlogCta && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Join the Tippo waitlist
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Be first to know when we open registration for your restaurant.
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="grid gap-2">
          <Label htmlFor="blog-waitlist-name">Name</Label>
          <Input
            id="blog-waitlist-name"
            type="text"
            placeholder="Your name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="blog-waitlist-email">Email</Label>
          <Input
            id="blog-waitlist-email"
            type="email"
            placeholder="you@restaurant.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
        {status === "error" && (
          <p className="text-sm text-destructive">{errorMessage}</p>
        )}
        <Button type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Joining…" : "Join the waitlist"}
        </Button>
      </form>
    </div>
  );
}
